import type { SurveyQuestion } from "@/types/survey";

/**
 * Loose, runtime-friendly view of a saved condition. We accept the
 * legacy/loose shape (any of these may be missing on disk) so the
 * evaluator can work against question payloads from any source — the
 * public survey, the editor preview, or older documents that don't yet
 * have every field populated.
 */
type LooseCondition = {
  var?: string;
  operator?: string;
  value?: unknown;
};

/**
 * Evaluate one condition against a map of answers (questionId → value).
 * Mirrors the per-condition semantics used on the public survey page:
 *
 * - A missing/empty target answer fails the condition.
 * - Multi-select arrays support only `==` and `!=` (substring of array).
 * - Numeric operators coerce both sides via `parseFloat`.
 *
 * Exposed for tests; consumers should prefer {@link shouldRenderQuestion}.
 */
export const evaluateCondition = (
  condition: LooseCondition,
  answers: Record<string, unknown>,
): boolean => {
  if (!condition || !condition.var) return false;
  const answerValue = answers[condition.var];
  if (
    answerValue === undefined ||
    answerValue === null ||
    (Array.isArray(answerValue) && answerValue.length === 0) ||
    (!Array.isArray(answerValue) && answerValue === "")
  ) {
    return false;
  }

  const operator = condition.operator || "==";
  const conditionValue = condition.value;

  if (Array.isArray(answerValue)) {
    const arr = (answerValue as unknown[]).map((v) => String(v));
    const condStr = String(conditionValue ?? "");
    if (operator === "==") return arr.includes(condStr);
    if (operator === "!=") return !arr.includes(condStr);
    // Numeric comparisons are not applicable for multi-select arrays
    return false;
  }

  const targetValueStr = String(answerValue);
  const conditionValueStr = String(conditionValue ?? "");
  switch (operator) {
    case "==":
      return targetValueStr === conditionValueStr;
    case "!=":
      return targetValueStr !== conditionValueStr;
    case "<":
      return parseFloat(targetValueStr) < parseFloat(conditionValueStr);
    case "<=":
      return parseFloat(targetValueStr) <= parseFloat(conditionValueStr);
    case ">":
      return parseFloat(targetValueStr) > parseFloat(conditionValueStr);
    case ">=":
      return parseFloat(targetValueStr) >= parseFloat(conditionValueStr);
    default:
      return false;
  }
};

/**
 * Decide whether a question should render given the current answers.
 *
 * Combines per-condition results using the question's
 * {@link SurveyQuestion.conditionLogic} field:
 *
 * - `"AND"` (default, legacy behavior) — every condition must match.
 * - `"OR"` — any single match is enough.
 *
 * Questions with no conditions always render. Existing questions
 * without `conditionLogic` are treated as `"AND"` so legacy surveys
 * keep their existing behavior.
 */
export const shouldRenderQuestion = (
  question: SurveyQuestion,
  answers: Record<string, unknown>,
): boolean => {
  const conds = (question.conditions || []) as unknown as LooseCondition[];
  if (!conds || conds.length === 0) return true;
  const logic =
    (question as unknown as { conditionLogic?: "AND" | "OR" }).conditionLogic ??
    "AND";
  return logic === "OR"
    ? conds.some((c) => evaluateCondition(c, answers))
    : conds.every((c) => evaluateCondition(c, answers));
};
